/**
 * Receipt image helpers
 *
 * Turns the on-screen `ReceiptView` DOM into a downloadable PNG without altering
 * its layout, styles or content — html-to-image clones the node and rasterizes it
 * exactly as rendered.
 */
import { toBlob } from 'html-to-image';

const API_ORIGIN = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

/**
 * Rewrites an uploaded-asset URL (business logo / QR) so its bytes can be read
 * cross-origin while rasterizing. The backend `/uploads` route normally 302-redirects
 * to a raw storage URL with no CORS headers, which taints the canvas; `?inline=1`
 * makes it stream the bytes through the API instead, where the global CORS policy
 * applies. Relative URLs are resolved against the API origin.
 * @param {string} rawUrl
 * @returns {string}
 */
export const toInlineAssetUrl = (rawUrl) => {
    if (!rawUrl) return rawUrl;
    try {
        const base = rawUrl.startsWith('/') ? (API_ORIGIN || window.location.origin) : undefined;
        const url = new URL(rawUrl, base);
        url.searchParams.set('inline', '1');
        return url.toString();
    } catch (e) {
        return rawUrl; // not a parseable URL — leave it untouched
    }
};

/**
 * Returns a copy of the sale with logo/QR URLs routed through the inline endpoint,
 * so the rasterized receipt keeps those images. Everything else is untouched.
 * @param {object} sale
 * @returns {object}
 */
export const withInlineAssets = (sale) => {
    if (!sale) return sale;
    return {
        ...sale,
        logo_url: toInlineAssetUrl(sale.logo_url),
        qr_code_url: toInlineAssetUrl(sale.qr_code_url),
    };
};

/**
 * Waits until every <img> inside `node` has finished loading (or errored), so the
 * snapshot doesn't capture half-loaded logos.
 * @param {HTMLElement} node
 */
const waitForImages = async (node) => {
    const images = Array.from(node.querySelectorAll('img'));
    await Promise.all(
        images.map((img) => {
            if (img.complete && img.naturalWidth > 0) return Promise.resolve();
            return new Promise((resolve) => {
                img.addEventListener('load', resolve, { once: true });
                img.addEventListener('error', resolve, { once: true });
            });
        })
    );
    try {
        await document.fonts?.ready;
    } catch (e) {
        // font loading is best-effort; capture proceeds with whatever is available
    }
};

/**
 * Rasterizes `node` to a PNG and triggers a browser download.
 * @param {HTMLElement} node - element wrapping the rendered receipt
 * @param {string} fileName - download file name (without extension)
 */
export const downloadReceiptImage = async (node, fileName) => {
    if (!node) throw new Error('Receipt node is not available');

    await waitForImages(node);

    const blob = await toBlob(node, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#ffffff',
    });
    if (!blob) throw new Error('Failed to rasterize the receipt');

    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = `${fileName}.png`;
    link.href = objectUrl;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Keep the blob URL alive long enough for the browser to start the download.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
};
